import React from 'react';
import { motion } from "motion/react";
import { useNavigate } from 'react-router-dom';
import type { CartResponse } from '@/features/cart/types';
import CartCard from './CartCard';
import CartCardSkeleton from '@/components/skeleton/CartCardSkeleton';

interface PreviousPurchasesSectionProps {
  carts: CartResponse[];
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
}

export const PreviousPurchasesSection: React.FC<PreviousPurchasesSectionProps> = ({ 
  carts, 
  loading, 
  error,
  onRetry 
}) => {
  const navigate = useNavigate();

  // Dummy delete handler - completed carts shouldn't be deletable
  const handleDelete = async () => {
    // No-op for completed carts
    console.log('Delete not allowed for completed carts');
  };

  return (
    <motion.div 
      className="lg:col-span-2"
      initial={{ opacity: 0, x: 50, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -50, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 300, damping: 30, mass: 0.8 }}
    >
      <div className="bg-gray-900 border border-gray-800 rounded-3xl p-4 sm:p-6 lg:p-8 h-auto sm:h-[600px] lg:h-[700px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6 lg:mb-8 flex-shrink-0">
          <div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-serif font-medium">
              Previous Purchases
            </h3>
            <div className="h-0.5 bg-violet-500 mt-1 sm:mt-2 w-16" />
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-y-visible sm:overflow-y-auto pr-1 sm:pr-2">
          {loading ? (
            <div className="space-y-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <CartCardSkeleton key={i} />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-10">
              <p className="text-red-400 mb-4">{error}</p>
              {onRetry && (
                <button 
                  onClick={onRetry} 
                  className="px-6 py-2 bg-violet-600 text-white rounded-full hover:bg-violet-500 transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          ) : carts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-10">
              <svg
                className="w-24 h-24 text-gray-600 mb-4 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                />
              </svg>
              <h2 className="text-2xl font-semibold text-white mb-2">
                No Previous Purchases
              </h2>
              <p className="text-gray-400 mb-6 max-w-md">
                You haven't made any purchases yet. Start browsing events and complete your first purchase!
              </p>
              <button
                onClick={() => navigate('/events')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Browse Events
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {carts.map((cart) => (
                <CartCard
                  key={cart.id}
                  cart={cart}
                  onDelete={handleDelete}
                  showDelete={false}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

